import type { SessionTransportSnapshot } from "./joinBootstrap";

export const LATE_JOIN_SECOND_PASS_DELAY_MS = 700;
export const LATE_JOIN_CORRECTION_DRIFT_THRESHOLD_MS = 45;
export const LATE_JOIN_MAX_PROJECTION_WINDOW_MS = 15000;

export interface LateJoinCorrectionPlan {
  readonly authoritativePositionSec: number;
  readonly localPositionSec: number;
  readonly beforeOffsetMs: number;
  readonly thresholdMs: number;
  readonly shouldCorrect: boolean;
  readonly reason: string;
}

export function projectAuthoritativePositionSec(
  snapshot: SessionTransportSnapshot,
  targetEpochMs: number,
): number {
  if (snapshot.state !== "playing" || snapshot.playbackRate <= 0) {
    return Math.max(0, snapshot.positionSec);
  }
  const projectionBaseEpochMs = snapshot.sampledAtEpochMs ?? snapshot.updatedAtEpochMs;
  const elapsedMs = Math.min(
    LATE_JOIN_MAX_PROJECTION_WINDOW_MS,
    Math.max(0, targetEpochMs - projectionBaseEpochMs),
  );
  return Math.max(0, snapshot.positionSec + (elapsedMs / 1000) * snapshot.playbackRate);
}

export function planLateJoinCorrection(input: {
  readonly snapshot: SessionTransportSnapshot;
  readonly localPositionSec: number;
  readonly correctionAtEpochMs: number;
  readonly thresholdMs?: number;
}): LateJoinCorrectionPlan {
  const thresholdMs = input.thresholdMs ?? LATE_JOIN_CORRECTION_DRIFT_THRESHOLD_MS;
  const authoritativePositionSec = projectAuthoritativePositionSec(
    input.snapshot,
    input.correctionAtEpochMs,
  );
  const localPositionSec = Math.max(0, input.localPositionSec);
  const beforeOffsetMs = (authoritativePositionSec - localPositionSec) * 1000;
  const driftAbsMs = Math.abs(beforeOffsetMs);
  const shouldCorrect = driftAbsMs > thresholdMs;
  const reason = shouldCorrect
    ? `drift ${driftAbsMs.toFixed(1)}ms exceeds ${thresholdMs.toFixed(1)}ms threshold`
    : `drift ${driftAbsMs.toFixed(1)}ms within ${thresholdMs.toFixed(1)}ms threshold`;
  return {
    authoritativePositionSec,
    localPositionSec,
    beforeOffsetMs,
    thresholdMs,
    shouldCorrect,
    reason,
  };
}
